import { useTranslation } from "react-i18next";
import { AED } from "../lib/api";
import { Loader2, Info, Check } from "lucide-react";

const INCLUDED = ["Collision Damage Waiver and third-party cover over 1 million USD", "Unlimited mileage", "24/7 roadside assistance", "Accident or breakdown replacement"];

export const PriceSummary = ({ quote, loading, ctaLabel, onCta, ctaDisabled, testIdPrefix = "summary" }) => {
  const { t } = useTranslation();
  if (!quote && !loading) return null;
  return (
    <div className="bg-white rounded-lg border border-[#e2ebe4] shadow-sm overflow-hidden" data-testid={`${testIdPrefix}-panel`}>
      <div className="bg-[#144733] text-white px-4 py-3 heading-upper text-sm tracking-widest">{t("Price Summary")}</div>
      <div className="p-4">
        {loading && !quote ? (
          <div className="flex items-center justify-center py-8 text-[#57685e]"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <>
            <div className={`space-y-2 ${loading ? "opacity-50" : ""}`}>
              {quote.lines.map((l) => (
                <div key={`${l.label}-${l.amount}`} className="flex justify-between text-sm text-[#0F1E17]">
                  <span className="pr-2">{t(l.label)}</span>
                  <span className={l.amount < 0 ? "text-[#009900] font-semibold" : "font-medium"}>{l.amount < 0 ? `− ${AED(-l.amount)}` : AED(l.amount)}</span>
                </div>
              ))}
              {quote.complimentary?.map((c) => (
                <div key={c.label} data-testid="complimentary-line">
                  <div className="flex justify-between text-sm text-[#8a978f]">
                    <span className="pr-2 line-through">{t(c.label)}</span>
                    <span className="line-through">{AED(c.amount)}</span>
                  </div>
                  <div className="text-[11px] text-[#009900] font-medium">{t(c.note)}</div>
                </div>
              ))}
              <div className="border-t border-dashed border-[#d5ddd5] pt-2 flex justify-between text-sm">
                <span>{t("Subtotal")}</span><span className="font-medium">{AED(quote.subtotal)}</span>
              </div>
              {quote.pay_adjustment.amount !== 0 && (
                <div className="flex justify-between text-sm">
                  <span className={quote.pay_adjustment.amount < 0 ? "text-[#009900]" : "text-amber-700"}>{t(quote.pay_adjustment.label)}</span>
                  <span className={quote.pay_adjustment.amount < 0 ? "text-[#009900] font-semibold" : "text-amber-700 font-medium"}>
                    {quote.pay_adjustment.amount < 0 ? `− ${AED(-quote.pay_adjustment.amount)}` : `+ ${AED(quote.pay_adjustment.amount)}`}
                  </span>
                </div>
              )}
              {quote.promo?.code && (
                <div className="flex justify-between text-sm text-[#009900]" data-testid="promo-line">
                  <span>{t("Promo {{code}}", { code: quote.promo.code })}</span><span className="font-semibold">− {AED(quote.promo.discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-[#57685e]">
                <span>{t("VAT ({{rate}})", { rate: quote.vat_rate })}</span><span>{AED(quote.vat)}</span>
              </div>
              <div className="border-t-2 border-[#144733] pt-3 flex justify-between items-baseline">
                <span className="heading-upper text-xs tracking-widest text-[#144733]">{t("Total")}</span>
                <span className="text-2xl font-bold text-[#0F1E17]" data-testid="quote-total">{AED(quote.total)}</span>
              </div>
            </div>

            <ul className="mt-4 space-y-1.5" data-testid="included-checklist">
              {INCLUDED.map((i) => (
                <li key={i} className="flex gap-2 text-xs text-[#144733]"><Check className="w-3.5 h-3.5 text-[#009900] shrink-0 mt-0.5" />{t(i)}</li>
              ))}
            </ul>

            <div className="mt-3 bg-[#f4f7f4] rounded p-3 text-xs text-[#144733]" data-testid="deposit-disclosure">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-[#009900]" />
                <div>
                  {quote.deposit.waived ? (
                    <span className="font-semibold text-[#009900]">{t("Security deposit waived (NDW active)")}</span>
                  ) : (
                    <>
                      <span className="font-semibold">{t("Security deposit:")}</span> {t("{{cc}} credit card pre-auth or {{dc}} debit card charge.", { cc: AED(quote.deposit.credit_card), dc: AED(quote.deposit.debit_card) })}
                      <div className="mt-1 text-[#57685e]" data-testid="hold-explainer">{t("A credit-card pre-authorisation is a temporary hold, not a charge, and it's released after you return the car. A debit-card deposit is charged and refunded after closure.")}</div>
                    </>
                  )}
                  <div className="mt-1 text-[#57685e]">
                    {t(quote.mileage_note)} · {t("Excess mileage AED {{rate}}/km + VAT", { rate: quote.excess_mileage_rate.toFixed(2) })}
                  </div>
                </div>
              </div>
            </div>

            {onCta && (
              <>
                <button onClick={onCta} disabled={ctaDisabled || loading} className="btn-cta rounded w-full py-3 mt-4 text-sm" data-testid="proceed-to-checkout-btn">
                  {ctaLabel}
                </button>
                <p className="text-[11px] text-[#8a978f] text-center mt-2" data-testid="no-charge-note">{t("No charge yet. You review everything on the next step.")}</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
